"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cnBind from "classnames/bind";
import { usePathname } from "next/navigation";

import { Navbar } from "@/components/NavBar";
import type { NavbarItem } from "@/components/NavBar/constants";
import { NavBarMob } from "@/components/NavBar/NavBarMob";
import { IcDots } from "@/shared/assests/svg";
import { CloseMenuIcon } from "@/shared/assests/svg/svg";
import { useBooleanState } from "@/shared/hooks";
import { resetPageInteractionLocks } from "@/shared/utils/resetPageInteractionLocks";

import { HeaderAuth } from "@/components/HeaderAuth/HeaderAuth";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

import styles from "./Header.module.scss";

const cx = cnBind.bind(styles);

type HeaderProps = {
    data: NavbarItem[];
};

export const Header = ({ data }: HeaderProps) => {
    const { t } = useTranslation("common");
    const [isOpen, open, close] = useBooleanState(false);
    const pathname = usePathname() ?? "/";
    const [isScrolled, setIsScrolled] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const [mounted, setMounted] = useState(false);
    const isOpenRef = useRef(isOpen);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        isOpenRef.current = isOpen;

        if (isOpen) {
            setIsHidden(false);
        }
    }, [isOpen]);

    // After client-side navigation Next may restore/reset scroll without a scroll event.
    // PrimeReact Dialog can also leave body { pointer-events: none } until a full reload.
    useEffect(() => {
        close();
        resetPageInteractionLocks();

        const syncHeaderVisibility = () => {
            const y = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
            setIsScrolled(y > 8);
            setIsHidden(false);
        };

        syncHeaderVisibility();
        const t0 = window.setTimeout(syncHeaderVisibility, 0);
        const t1 = window.setTimeout(syncHeaderVisibility, 120);
        const t2 = window.requestAnimationFrame(syncHeaderVisibility);
        const onPageShow = () => {
            resetPageInteractionLocks();
            syncHeaderVisibility();
        };
        window.addEventListener("pageshow", onPageShow);

        return () => {
            window.clearTimeout(t0);
            window.clearTimeout(t1);
            window.cancelAnimationFrame(t2);
            window.removeEventListener("pageshow", onPageShow);
        };
    }, [pathname, close]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                close();
            }
        };

        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [close, isOpen]);

    useEffect(() => {
        const getScrollY = () =>
            window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

        let lastY = getScrollY();
        let ticking = false;
        let allowHide = false;
        // Ignore hide-on-scroll until Next finishes route scroll restoration.
        const enableHideTimer = window.setTimeout(() => {
            allowHide = true;
            lastY = getScrollY();
        }, 400);

        const getScrollConfig = () => {
            const isMobile = window.innerWidth <= 1100;
            return {
                topRevealY: isMobile ? 12 : 24,
                hideAfterY: isMobile ? 56 : 120,
                hideDelta: isMobile ? 4 : 8,
            };
        };

        const updateHeaderOnScroll = () => {
            const currentY = getScrollY();
            const { topRevealY, hideAfterY, hideDelta } = getScrollConfig();

            setIsScrolled(currentY > 8);

            if (isOpenRef.current || currentY <= topRevealY || !allowHide) {
                setIsHidden(false);
                lastY = currentY;
                ticking = false;
                return;
            }

            if (currentY > lastY + hideDelta && currentY > hideAfterY) {
                setIsHidden(true);
            } else if (currentY < lastY) {
                setIsHidden(false);
            }

            lastY = currentY;
            ticking = false;
        };

        const onScroll = () => {
            if (ticking) {
                return;
            }
            ticking = true;
            window.requestAnimationFrame(updateHeaderOnScroll);
        };

        const onWheel = (event: WheelEvent) => {
            if (event.deltaY < 0 && !isOpenRef.current) {
                setIsHidden(false);
            }
        };

        const onPointerMove = (event: PointerEvent) => {
            if (event.clientY <= 28) {
                setIsHidden(false);
            }
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        document.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("wheel", onWheel, { passive: true });
        window.addEventListener("pointermove", onPointerMove, { passive: true });
        setIsHidden(false);
        setIsScrolled(getScrollY() > 8);

        return () => {
            window.clearTimeout(enableHideTimer);
            window.removeEventListener("scroll", onScroll);
            document.removeEventListener("scroll", onScroll);
            window.removeEventListener("wheel", onWheel);
            window.removeEventListener("pointermove", onPointerMove);
        };
    }, [pathname]);

    const mobileMenuPortal =
        mounted && isOpen
            ? createPortal(
                  <div
                      className={cx("mobile-menu")}
                      role="dialog"
                      aria-modal="true"
                      aria-label={t("settings.header.navAriaLabel")}
                  >
                      <div className={cx("mobile-menu-shell", "bg-liquid-glass", "glass-specular", "glass-noise")}>
                          <div className={cx("mobile-menu-top")}>
                              <a href="/" className={cx("brand", "mobile-menu-brand")} onClick={close} aria-label="TEZAM">
                                  <img src="/images/tezam-logo.png" alt="TEZAM" />
                              </a>
                              <button
                                  type="button"
                                  className={cx("mobile-menu-close")}
                                  onClick={close}
                                  aria-label={t("settings.header.closeMenu")}
                              >
                                  <CloseMenuIcon className={cx("mobile-menu-close-icon")} />
                              </button>
                          </div>

                          <div className={cx("mobile-menu-body")}>
                              <NavBarMob data={data} onClick={close} />
                          </div>

                          <div className={cx("mobile-menu-footer")}>
                              <div className={cx("mobile-menu-lang")}>
                                  <LanguageSwitcher />
                              </div>
                              <a href="/auth?tab=register&next=/cabinet" className={cx("mobile-menu-start")} onClick={close}>
                                  {t("settings.header.register")}
                              </a>
                              <a href="/auth?tab=login&next=/cabinet" className={cx("mobile-menu-login")} onClick={close}>
                                  {t("settings.header.login")}
                              </a>
                              <a
                                  href="/auth?tab=register&next=/cabinet/create"
                                  className={cx("mobile-menu-phone")}
                                  onClick={close}
                              >
                                  {t("settings.header.createFree")}
                              </a>
                          </div>
                      </div>
                  </div>,
                  document.body,
              )
            : null;

    return (
        <>
            <div
                className={cx("header-reveal-zone")}
                onMouseEnter={() => setIsHidden(false)}
                onPointerDown={() => setIsHidden(false)}
                aria-hidden
            />
            <header
                className={cx(
                    "header",
                    "bg-liquid-glass",
                    "glass-specular",
                    isScrolled && "scrolled",
                    isHidden && "hidden",
                    isOpen && "menu-open",
                )}
                data-fixed-overlay="true"
            >
                <div className={cx("wrapper")}>
                    <a href="/" className={cx("brand")} aria-label="TEZAM">
                        <img src="/images/tezam-logo.png" alt="TEZAM" />
                    </a>

                    <div className={cx("nav", "nav-desktop")}>
                        <Navbar data={data} className={cx("navbar-header")} />
                    </div>

                    <div className={cx("connect")}>
                        <LanguageSwitcher className={cx("lang")} />
                        <HeaderAuth className={cx("headerAuth")} />
                        <button
                            type="button"
                            className={cx("menu-toggle", "nav-mobile-toggle", isOpen && "menu-toggle-active")}
                            onClick={isOpen ? close : open}
                            aria-label={isOpen ? t("settings.header.closeMenu") : t("settings.header.openMenu")}
                            aria-expanded={isOpen}
                        >
                            {isOpen ? (
                                <CloseMenuIcon className={cx("icon")} />
                            ) : (
                                <IcDots className={cx("icon")} />
                            )}
                        </button>
                    </div>
                </div>
            </header>
            {mobileMenuPortal}
        </>
    );
};
